// apps/web/src/features/users/components/UserResetPasswordModal.tsx
// target user; backend hashes and stores it, sets
// `passwordResetRequired = true`, returns `{ ok: true }`. No reveal step.
//
// Issue #628 PR3 (now #680): close paths (Modal chrome X, Escape, backdrop,
// Cancel button) route through the EditorModal wrapper guard. Per
// Decision 13 dirty means the typed `newPassword` differs from its empty
// default — implemented via RHF `formState.isDirty` since the empty string
// IS the only baseline. SECURITY: only boolean `dirty`/`busy` and the
// close callback cross the guard boundary; the typed password value never
// leaves the form.
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ResetPasswordInputSchema, type ResetPasswordInput, type User } from '@magimp/shared';
import { useResetUserPassword } from '@magimp/shared-hooks';
import { EditorModal, Button, FormRow, useEditorGuard } from '../../../ui/index.js';
import { PasswordField } from '../../auth/components/PasswordField.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

export interface UserResetPasswordModalProps {
  user: User;
  onClose: () => void;
}

export function UserResetPasswordModal({ user, onClose }: UserResetPasswordModalProps) {
  const { t } = useTranslation(['users', 'common']);
  const reset = useResetUserPassword(user.id);
  const show = useToastStore((s) => s.show);
  const {
    control,
    handleSubmit,
    reset: formReset,
    formState: { errors, isDirty },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(ResetPasswordInputSchema),
    defaultValues: { newPassword: '' },
  });
  const onSubmit = (values: ResetPasswordInput) => {
    reset.mutate(values, {
      onSuccess: () => {
        show({
          tone: 'success',
          text: t('users:toasts.resetPassword'),
        });
        // Re-baseline so post-success close path does not warn.
        formReset({ newPassword: '' });
        onClose();
      },
      onError: (err) => {
        show({ tone: 'error', text: errorMessage(err) });
      },
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('users:modals.resetPasswordTitle', { name: user.name })}
      width={420}
      dirty={isDirty}
      busy={reset.isPending}
      onConfirmClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <FormRow label={t('users:fields.newPassword')} error={errors.newPassword?.message}>
          <Controller
            control={control}
            name="newPassword"
            render={({ field }) => (
              <PasswordField
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.newPassword}
                autoComplete="new-password"
                showGenerate
                showCopy
              />
            )}
          />
        </FormRow>
      </form>
      <EditorFooter>
        <CancelButton
          onClose={onClose}
          label={t('common:actions.cancel')}
          disabled={reset.isPending}
        />
        <Button
          kind="primary"
          onClick={() => void handleSubmit(onSubmit)()}
          disabled={reset.isPending}
        >
          {t('users:actions.reset')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

// WHY: Cancel must call `useEditorGuard().confirmDestructive(onClose)` to
// prompt on dirty close. The EditorGuardContext provider only wraps the
// EditorModal `children` subtree, not the `footer` prop, so the action
// buttons are rendered inline at the bottom of children. `EditorFooter`
// mirrors the visual style of the Modal footer slot.
function EditorFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      {children}
    </div>
  );
}

function CancelButton({
  onClose,
  label,
  disabled,
}: {
  onClose: () => void;
  label: string;
  disabled?: boolean;
}) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)} disabled={disabled ?? false}>
      {label}
    </Button>
  );
}
